import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KEY_RECOIL, DEVICE_TAB_TYPES } from '@/shared/config';
import { isValidHttpUrl } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import { checkStatusStripChange, SAVED_SRIPS_IMAGES } from '@/widgets/strips-management/ui/handler';
import {
  selectChangeStripData,
  selectDeviceEventData,
  selectEventStripsData,
  selectIsLoadingStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { StripImageViewerProps } from './index';

/**
 * Backs the strip image gallery beside the strips table, including its edit modal. It resolves the viewed strip from any pending change or the store selection, rebuilds the gallery items through the cache-aware image resolver whenever the images change, tracks image load progress for the spinner, and folds an accepted edit back into the tab's change list via the strip change checker.
 *
 * @param props - Strip image viewer props carrying the active device tab button.
 * @returns An object with the strip and save loading flags, local state and its setter, the gallery items, the image ref, the viewed strip, the device tab flag and the edit, close and update handlers.
 */
const useStripImageViewer = ({ activeButton }: Pick<StripImageViewerProps, 'activeButton'>) => {
  const savedImageRef = useRef<any>({});
  const imageRef = useRef<any>();

  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const isSavingStripManagement = useReportStore((s) => s.isSavingStripmanagement);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const eventStripsData = useReportStore(selectEventStripsData(keyRecoil));
  const deviceEventsData = useReportStore(selectDeviceEventData(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Writes the pending strip change list for report tab 3 into the shared report store. It calls the store action with the tab's key bound in front of the caller's value.
   *
   * @param v - New value or updater for the pending strip changes.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);

  const [state, setState] = useMergeState<{
    isLoading: boolean;
    isLoadedImageError: boolean;
    stripImgs?: any[];
    isVisibleEditAiStripModal?: boolean;
    selectedData?: any;
    originalSelectedData?: any;
  }>({
    isLoading: true,
    isLoadedImageError: false,
  });

  const [listImages, setListImages] = useState<any[]>([]);

  const isDeviceEvent = useMemo(() => DEVICE_TAB_TYPES.includes(activeButton), [activeButton]);

  const viewSelectedStrip = useMemo(() => {
    const changedStrip = _.find(changeStripData, (x) => x.id === selectedStrip?.id);
    // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark; originalInfo
    // is only on the local strip shapes built in StripsManagementV2/handler.ts - pre-existing gap.
    const { stripImgs } = changedStrip || (selectedStrip as any)?.originalInfo || {};
    if (stripImgs) {
      setState({ stripImgs });
    } else {
      setState({ stripImgs: [] });
    }
    return changedStrip || selectedStrip;
  }, [selectedStrip, changeStripData]);

  /**
   * Resolves a remote strip image URL to a local blob URL when its markup was pre-downloaded. It looks the URL up in the shared SAVED_SRIPS_IMAGES cache and wraps the cached SVG markup in a blob URL, falling back to the original URL on a miss.
   *
   * @param stripImg - Strip image URL to resolve.
   * @returns A blob URL for the cached SVG, or the original URL when it was not cached.
   */
  const findCachedImage = (stripImg: any) => {
    const cachedImg = _.find(SAVED_SRIPS_IMAGES, (x) => x.baseUrl === stripImg);
    // return cachedImg?.objectUrl ? `data:image/svg+xml;base64,${btoa(cachedImg?.objectUrl)}` : stripImg;
    if (cachedImg?.objectUrl) {
      const svgBlob = new Blob([cachedImg?.objectUrl], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      return svgUrl;
    }
    return stripImg;
  };

  /**
   * Builds the gallery items for one strip's images. It drops the combined all.svg entry, resolves each remaining image through the download cache when it is a URL or wraps inline markup in a blob URL, and labels every item with its position in the set.
   *
   * @param stripImgs - Strip image URLs or inline SVG markup.
   * @returns The gallery items carrying original, thumbnail and a position description.
   */
  const createItems = (stripImgs: any[]) => {
    const notIncludedAllStripImgs = _.filter(stripImgs, (x) => !_.includes(x, 'all.svg'));
    const imgs = _.map(notIncludedAllStripImgs, (stripImg, index) => {
      // const formatImg = isValidHttpUrl(stripImg) ? findCachedImage(stripImg) : `data:image/svg+xml;base64,${btoa(stripImg)}`;
      const svgBlob = new Blob([stripImg], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const formatImg = isValidHttpUrl(stripImg) ? findCachedImage(stripImg) : svgUrl;
      const description = `${index + 1}/${notIncludedAllStripImgs.length}`;
      return {
        original: formatImg,
        originalAlt: `ECG strip ${description}`,
        thumbnail: formatImg,
        thumbnailAlt: `ECG strip ${description} thumbnail`,
        description,
      };
    });
    return imgs;
  };
  /**
   * Closes the strip edit modal. It hides the modal and clears both the selected strip and its original snapshot from local state.
   *
   * @returns Nothing.
   */
  const closeEditStripModal = () => {
    setState({
      isVisibleEditAiStripModal: false,
      selectedData: {},
      originalSelectedData: {},
    });
  };

  /**
   * Opens the edit modal for the strip currently selected in the table. It looks the untouched strip up in the device event or event strip data for the active tab, prefers any pending change over the stored strip as the editable copy, and puts both on local state before showing the modal.
   *
   * @returns Nothing.
   */
  const openEditStripModal = () => {
    const originalSelectedData = selectedStrip
      ? _.find(
          isDeviceEvent ? (deviceEventsData as any)?.[activeButton] : eventStripsData,
          (x) => x.id === (selectedStrip as any).id,
        )
      : undefined;
    const changedStrip = _.find(changeStripData, (x) => x.id === selectedStrip?.id);
    setState({
      isVisibleEditAiStripModal: true,
      selectedData: changedStrip || selectedStrip,
      originalSelectedData,
    });
  };

  /**
   * Handles the edit button on the strip image viewer. It opens the edit modal for the currently selected strip.
   *
   * @returns Nothing.
   */
  const onClickEditStrip = () => {
    openEditStripModal();
  };

  /**
   * Applies the result of the strip edit modal to the tab's pending change list. An empty patch merely closes the modal, otherwise it clones the viewed strip, merges the patch, runs the change checker against the existing changes and writes the result back only when it actually differs, then closes the modal.
   *
   * @param updateData - Patch returned by the edit modal, empty when the user cancelled.
   * @returns Nothing.
   */
  const updateEditData = (updateData: any) => {
    if (_.isEmpty(updateData)) {
      closeEditStripModal();
      return;
    }
    const newUpdate = _.cloneDeep(viewSelectedStrip);
    _.assign(newUpdate, updateData);
    const changes = checkStatusStripChange([newUpdate], changeStripData);
    setChangeStripData((prev) => {
      if (_.isEqual(changes, prev)) {
        return prev;
      }
      return changes;
    });
    closeEditStripModal();
  };

  /**
   * Settles the loading spinner over the strip image gallery. It clears the flag once every current strip image appears in the gallery's loaded set, or as soon as one image is inline markup rather than a URL, or when an image has already been seen before, recording each image in the seen ref either way.
   *
   * @returns Nothing.
   */
  const checkLoading = () => {
    if (!_.isEmpty(imageRef.current?.loadedImages) && !_.isEmpty(state.stripImgs)) {
      setState({ isLoading: true });
      const loadedImgArr = Object.keys(imageRef.current?.loadedImages);
      if (_.every(state.stripImgs, (item) => loadedImgArr.includes(item))) {
        setState({ isLoading: false });
        return;
      }
      if (_.some(state.stripImgs, (x) => x && !isValidHttpUrl(x))) {
        setState({ isLoading: false });
        return;
      }
      _.forEach(state.stripImgs, (item) => {
        if (!savedImageRef.current[item]) {
          savedImageRef.current[item] = item;
        } else {
          setState({ isLoading: false });
        }
      });
    }
    _.forEach(state.stripImgs, (item) => {
      if (!savedImageRef.current[item]) {
        savedImageRef.current[item] = item;
      }
    });
  };

  useEffect(() => {
    setState({ isLoadedImageError: false });
    checkLoading();
    if (state.stripImgs?.length) {
      const stripImgs = createItems(state.stripImgs);
      setListImages(stripImgs);
    }
  }, [JSON.stringify(state.stripImgs)]);

  return {
    isLoadingStrip,
    isSavingStripManagement,
    state,
    setState,
    listImages,
    imageRef,
    viewSelectedStrip,
    isDeviceEvent,
    onClickEditStrip,
    closeEditStripModal,
    updateEditData,
  };
};

export default useStripImageViewer;
